import { ROLE_INTRO_CONTENT, type RoleIntroRoleKey } from './role-intro-content.ts';

/**
 * Which role's first-login walkthrough (Practice Compass) to show, if any.
 *
 * A person can hold several practice roles at once (owner on one practice, practitioner on
 * another); this shows at most ONE overlay at a time, picked by how much a role changes what
 * the person does in HapMetrix - the order below, most to least. Only roles that HAVE content
 * (owner, practitioner, client_contact, support_worker) are ever candidates; admin/observer are
 * silently skipped, same as a role already dismissed.
 */
const ROLE_PRIORITY: readonly RoleIntroRoleKey[] = [
  'owner',
  'practitioner',
  'support_worker',
  'client_contact',
];


/**
 * WHICH walkthrough is due — pure, so the at-most-one rule and the priority order are testable
 * without a DOM. `optimistic` is a SET: with a single slot, dismissing the second role of a
 * dual-persona account stopped excluding the first and a double-click brought it back until
 * `/me` returned.
 */
export function nextRoleIntroRole(
  held: ReadonlySet<string>,
  dismissed: ReadonlySet<string>,
  optimistic: ReadonlySet<RoleIntroRoleKey>,
): RoleIntroRoleKey | undefined {
  // Never the owner's on first login: it opened over setup and competed with the dashboard's
  // "Add your first client", which now does its job (owner 2026-10-27). Help still replays it.
  return ROLE_PRIORITY.find(
    (r) => r !== 'owner' && !optimistic.has(r) && held.has(r) && !dismissed.has(r) && ROLE_INTRO_CONTENT[r] !== undefined,
  );
}

/**
 * The query parameter that REOPENS a walkthrough, from the Help Centre or a link. The prototype
 * promised "reopen anytime from Help" and nothing delivered it, so the only way to see a
 * walkthrough again was a database edit.
 */
export const ROLE_INTRO_REPLAY_PARAM = 'walkthrough';

/** Roles this person can ask to see again: held, and authored. Dismissal is irrelevant here. */
export function replayableRoleIntros(held: ReadonlySet<string>): RoleIntroRoleKey[] {
  return ROLE_PRIORITY.filter((r) => held.has(r) && ROLE_INTRO_CONTENT[r] !== undefined);
}

/**
 * Which walkthrough a `?walkthrough=` value asks for, or undefined. Deliberately NOT trusting
 * the URL: a role the person does not hold would describe someone else's job as if it were
 * theirs, so the request is answered from the same held-and-authored set the Help Centre offers.
 */
export function replayRoleIntro(
  requested: string | null,
  held: ReadonlySet<string>,
): RoleIntroRoleKey | undefined {
  if (requested === null) return undefined;
  return replayableRoleIntros(held).find((r) => r === requested);
}
